import React, { useEffect, useState } from 'react';

/**
 * 加载组件
 * 结合toast风格和项目主题色的优雅loading效果
 * 使用fixed定位，居中在可视窗口中心，排除左侧菜单栏宽度
 * @param {string} message - 加载提示文本，默认为 "Loading..."
 */
const Loading = ({ message = 'Loading...' }) => {
  const [sidebarWidth, setSidebarWidth] = useState(320); // 默认320px (w-80)

  useEffect(() => {
    const updateSidebarWidth = () => {
      let foundWidth = 320; // 默认值（展开状态）

      // 方法1：查找sidebar容器（fixed定位，top-0 left-0）
      const sidebarContainers = document.querySelectorAll('div');

      for (const container of sidebarContainers) {
        const classes = container.className || '';
        const rect = container.getBoundingClientRect();

        // 查找sidebar外层容器
        if (classes.includes('fixed') && classes.includes('top-0') && classes.includes('left-0') && rect.left === 0) {
          // 在这个容器内查找实际的sidebar div（有bg-white和h-screen类）
          const innerDivs = container.querySelectorAll('div');
          for (const innerDiv of innerDivs) {
            const innerClasses = innerDiv.className || '';
            const innerRect = innerDiv.getBoundingClientRect();

            // 查找包含bg-white和h-screen的div
            if (
              innerClasses.includes('bg-white') &&
              innerClasses.includes('h-screen') &&
              innerRect.width > 0 &&
              innerRect.left >= 0 &&
              innerRect.left < 100 // 确保是左侧的sidebar
            ) {
              // 精确检测宽度：w-16是64px，w-80是320px
              // 由于可能有transition动画，使用更精确的范围
              const width = innerRect.width;
              if (width >= 60 && width <= 80) {
                // 折叠状态：w-16 (64px)
                foundWidth = 64;
                setSidebarWidth(64);
                return;
              } else if (width >= 300 && width <= 340) {
                // 展开状态：w-80 (320px)
                foundWidth = 320;
                setSidebarWidth(320);
                return;
              }
            }
          }
        }
      }

      // 方法2：如果方法1没找到，直接查找所有fixed定位的div，找最左侧的
      if (foundWidth === 320) {
        for (const div of sidebarContainers) {
          const classes = div.className || '';
          const rect = div.getBoundingClientRect();

          if (
            classes.includes('fixed') &&
            rect.left === 0 &&
            rect.width > 0 &&
            rect.height > window.innerHeight * 0.5 // 确保是sidebar（高度足够）
          ) {
            const width = rect.width;
            // 检测折叠或展开状态
            if (width >= 60 && width <= 80) {
              foundWidth = 64;
              break;
            } else if (width >= 300 && width <= 340) {
              foundWidth = 320;
              break;
            }
          }
        }
      }

      setSidebarWidth(foundWidth);
    };

    // 初始延迟，确保DOM已渲染
    const timer = setTimeout(updateSidebarWidth, 100);
    window.addEventListener('resize', updateSidebarWidth);

    // 监听DOM变化，因为sidebar宽度可能会动态改变（折叠/展开）
    const observer = new MutationObserver(() => {
      setTimeout(updateSidebarWidth, 50);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateSidebarWidth);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      className='fixed bg-white bg-opacity-10 flex items-center justify-center z-50'
      style={{
        top: 0,
        bottom: 0,
        left: `${sidebarWidth}px`,
        right: 0,
      }}
    >
      <div className='bg-white rounded-lg shadow-lg p-8 flex flex-col items-center min-w-[280px] border border-gray-200'>
        {/* Spinner动画 */}
        <div className='relative mb-4'>
          <div className='animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-green-500'></div>
          <div className='absolute inset-0 flex items-center justify-center'>
            <div className='rounded-full h-6 w-6 bg-green-500 opacity-20 animate-pulse'></div>
          </div>
        </div>

        {/* 加载文本 */}
        <p className='text-gray-700 font-medium text-lg'>{message}</p>

        {/* 装饰性点动画 */}
        <div className='flex space-x-1 mt-3'>
          <div className='h-2 w-2 bg-green-500 rounded-full animate-bounce' style={{ animationDelay: '0ms' }}></div>
          <div className='h-2 w-2 bg-green-500 rounded-full animate-bounce' style={{ animationDelay: '150ms' }}></div>
          <div className='h-2 w-2 bg-green-500 rounded-full animate-bounce' style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  );
};

export default Loading;
